import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Target, Users, Award, Zap, Sparkles, ArrowRight } from 'lucide-react';
import ParticleBackground from '../components/ParticleBackground';

const About = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-in');
    elements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) {
        elements.forEach((el) => observerRef.current.unobserve(el));
      }
    };
  }, []);

  const values = [
    {
      icon: <Target size={40} />,
      title: 'Excellence',
      description: 'We strive for perfection in every project, ensuring the highest quality standards.',
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      icon: <Users size={40} />,
      title: 'Collaboration',
      description: 'We work closely with our clients, treating them as partners in the journey.',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: <Award size={40} />,
      title: 'Innovation',
      description: 'We stay ahead of the curve, using cutting-edge technologies and best practices.',
      gradient: 'from-green-500 to-emerald-500',
    },
    {
      icon: <Zap size={40} />,
      title: 'Results',
      description: 'We focus on delivering measurable outcomes that drive business growth.',
      gradient: 'from-orange-500 to-red-500',
    },
  ];

  return (
    <div className="pt-16 min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-purple-600 to-pink-600 text-white py-20 min-h-[60vh] flex items-center overflow-hidden">
        <ParticleBackground />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(139,92,246,0.2),transparent_50%)]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center fade-in">
            <div className="inline-flex items-center gap-2 mb-6">
              <Sparkles className="text-yellow-300 animate-sparkle" size={20} />
              <span className="text-sm font-semibold bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                Since 2020
              </span>
              <Sparkles className="text-yellow-300 animate-sparkle" size={20} style={{ animationDelay: '1s' }} />
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-slide-up">
              About <span className="bg-gradient-to-r from-yellow-300 via-pink-300 to-purple-300 bg-clip-text text-transparent">BluePeak</span> Agency
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto animate-slide-up" style={{ animationDelay: '0.2s' }}>
              Building exceptional digital experiences since 2020
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 relative z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="fade-in bg-white rounded-xl shadow-xl p-8 md:p-12 border border-gray-100 hover:shadow-2xl transition-all duration-300">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our <span className="text-primary-600">Story</span>
            </h2>
            <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
              <p className="fade-in" style={{ animationDelay: '0.1s' }}>
                BluePeak Agency was founded with a simple mission: to help businesses succeed in
                the digital world by creating exceptional web experiences. What started as a small
                team of passionate developers has grown into a full-service digital agency.
              </p>
              <p className="fade-in" style={{ animationDelay: '0.2s' }}>
                Over the years, we've had the privilege of working with startups, growing
                businesses, and established enterprises across various industries. Each project has
                taught us something new and helped us refine our approach to delivering solutions
                that truly make a difference.
              </p>
              <p className="fade-in" style={{ animationDelay: '0.3s' }}>
                Today, we combine technical expertise with creative thinking to build websites,
                applications, and digital platforms that not only look great but also drive real
                business results. We believe in long-term partnerships and are committed to our
                clients' success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="text-primary-600">Mission</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              To empower businesses with cutting-edge digital solutions that drive growth,
              enhance user experiences, and create lasting value in an ever-evolving digital
              landscape.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="text-primary-600">Values</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="group fade-in bg-white p-8 rounded-xl shadow-lg text-center hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 tech-glow border border-gray-100"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`relative mb-4 inline-block p-4 rounded-xl bg-gradient-to-br ${value.gradient} transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                  <div className="text-white relative z-10 flex justify-center">{value.icon}</div>
                  <div className="absolute inset-0 bg-white/20 rounded-xl blur-xl group-hover:blur-2xl transition-all"></div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="text-primary-600">Team</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Meet the talented individuals behind BluePeak
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                initials: 'JD',
                name: 'John Doe',
                role: 'Founder & CEO',
                description: 'With over 10 years of experience in web development and digital strategy, John leads the vision and direction of BluePeak Agency.',
              },
              {
                initials: 'DE',
                name: 'Developer Engineer',
                role: 'Lead Engineer',
                description: 'Our lead engineer brings expertise in modern web technologies and architecture, ensuring every project is built to scale and perform.',
              },
            ].map((member, index) => (
              <div
                key={index}
                className="group fade-in bg-gray-50 p-8 rounded-xl shadow-lg text-center hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative w-32 h-32 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full mx-auto mb-6 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-white text-4xl font-bold relative z-10">{member.initials}</span>
                  <div className="absolute inset-0 bg-primary-300 rounded-full blur-2xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                  {member.name}
                </h3>
                <p className="text-primary-600 font-semibold mb-4">{member.role}</p>
                <p className="text-gray-600 leading-relaxed">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative bg-gradient-to-r from-primary-600 via-purple-600 to-pink-600 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.1),transparent)]"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 animate-slide-up">Ready to Work Together?</h2>
          <p className="text-xl text-white/90 mb-8 animate-slide-up" style={{ animationDelay: '0.2s' }}>
            Let's discuss how we can help bring your digital vision to life.
          </p>
          <Link
            to="/contact"
            className="group inline-flex items-center gap-2 bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-primary-50 transition-all duration-300 shadow-2xl hover:shadow-primary-500/50 hover:scale-105 tech-glow animate-slide-up"
            style={{ animationDelay: '0.4s' }}
          >
            Get In Touch
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
